export const GRAPH_PERIOD_BY_FILTER = Object.freeze({
  last7DaysEarnings: "last7Days",
  last14DaysEarnings: "last14Days",
  last30DaysEarnings: "last30Days",
  last60DaysEarnings: "last60Days",
  last6MonthEarnings: "last6Months",
  lastYearEarnings: "lastYear",
  YTD: "ytd",
  custom: "custom",
});

const METRIC_KEYS = [
  "recordedGrossCollectionsCents",
  "recordedBookingCollectionsCents",
  "recordedSubscriptionCollectionsCents",
  "recordedRefundsCents",
  "disputeWithdrawnCents",
  "disputeReinstatedCents",
  "disputeNetImpactCents",
  "recordedProcessingFeesCents",
  "recordedPlatformGrossRevenueCents",
  "recordedPlatformRefundAdjustmentsCents",
  "recordedPlatformNetRevenueCents",
  "hostAvailableCents",
  "hostOwedCents",
  "hostReservedCents",
  "hostInTransitCents",
  "hostPaidCents",
  "hostTransferredToConnectedCents",
  "hostReconciliationRequiredCents",
  "hostPlatformHeldLiabilityCents",
  "stripeAvailableCashCents",
  "platformAvailableCashBeforePayoutFeesCents",
  "reconciliationVarianceCents",
];

function integerOrNull(value) {
  return Number.isSafeInteger(value) ? value : null;
}

function normalizeMetrics(metrics = {}) {
  return METRIC_KEYS.reduce(
    (result, key) => ({ ...result, [key]: integerOrNull(metrics[key]) }),
    {}
  );
}

function normalizeComparison(comparison = {}) {
  return {
    recordedPlatformNetRevenueCents: integerOrNull(
      comparison.recordedPlatformNetRevenueCents
    ),
    complete: comparison.complete === true,
  };
}

export function graphPeriodForFilter(filter) {
  return GRAPH_PERIOD_BY_FILTER[filter] || "last7Days";
}

export function buildFinanceSummaryRequest({
  graphFilter,
  page,
  limit = 10,
  reservationId,
  fromDate,
  toDate,
}) {
  const period = graphPeriodForFilter(graphFilter);
  if (period === "custom") {
    if (!fromDate || !toDate || fromDate > toDate) {
      throw new Error("Choose a valid custom date range with From on or before To.");
    }
  }

  return {
    period,
    page,
    limit,
    reservationId: String(reservationId || "").trim(),
    currency: "usd",
    ...(period === "custom" ? { fromDate, toDate } : {}),
  };
}

export function normalizeFinanceSummaryResponse(response) {
  const payload = response?.data || response || {};
  if (payload.schemaVersion !== 1) {
    throw new Error("The finance summary response version is unsupported.");
  }

  const activity = payload.commissionActivity || {};
  const pagination = activity.pagination || {};
  const reconciliation = payload.reconciliation || {};
  return {
    ...payload,
    currency: String(payload.currency || "usd").toLowerCase(),
    metrics: normalizeMetrics(payload.metrics),
    graph: {
      ...(payload.graph || {}),
      points: Array.isArray(payload.graph?.points)
        ? payload.graph.points.filter(
            (point) =>
              typeof point?.period === "string" &&
              Number.isSafeInteger(point?.valueCents)
          )
        : [],
    },
    comparisons: {
      last7Days: normalizeComparison(payload.comparisons?.last7Days),
      last30Days: normalizeComparison(payload.comparisons?.last30Days),
      ytd: normalizeComparison(payload.comparisons?.ytd),
    },
    commissionActivity: {
      records: Array.isArray(activity.records) ? activity.records : [],
      pagination: {
        page: Number.isSafeInteger(pagination.page) ? pagination.page : 1,
        limit: Number.isSafeInteger(pagination.limit) ? pagination.limit : 10,
        totalRecords: Number.isSafeInteger(pagination.totalRecords)
          ? pagination.totalRecords
          : 0,
        totalPages: Number.isSafeInteger(pagination.totalPages)
          ? pagination.totalPages
          : 0,
      },
    },
    reconciliation: {
      ...reconciliation,
      reasonCodes: Array.isArray(reconciliation.reasonCodes)
        ? reconciliation.reasonCodes
        : [],
    },
  };
}

export function formatCents(value, currency = "usd") {
  if (!Number.isSafeInteger(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(value / 100);
}

export function getFinanceSummaryErrorMessage(error) {
  if (typeof error === "string" && error.trim()) return error;
  return (
    error?.response?.data?.message ||
    error?.response?.data?.userMessage ||
    error?.message ||
    "Failed to load the finance summary."
  );
}

export function payoutIsBlocked(summary) {
  const reconciliation = summary?.reconciliation || {};
  return Boolean(
    !summary ||
      typeof reconciliation.hostPayoutBlocked !== "boolean" ||
      typeof reconciliation.providerFinancePayoutBlocked !== "boolean" ||
      reconciliation.hostPayoutBlocked ||
      reconciliation.providerFinancePayoutBlocked ||
      !Number.isSafeInteger(
        summary?.metrics?.platformAvailableCashBeforePayoutFeesCents
      ) ||
      summary.metrics.platformAvailableCashBeforePayoutFeesCents <= 0
  );
}

export function normalizeCommissionActivityResponse(response) {
  const payload = response?.data || response;
  const pagination = payload?.pagination;
  if (payload?.schemaVersion !== 1 || !Array.isArray(payload.records) ||
      !pagination || ![pagination.page, pagination.limit, pagination.totalRecords, pagination.totalPages].every(Number.isSafeInteger) ||
      pagination.page < 1 || pagination.limit < 1 || pagination.totalRecords < 0 || pagination.totalPages < 0 ||
      !payload.asOf || !Number.isFinite(Date.parse(payload.asOf)) || !timezoneIsValid(payload.timezone)) {
    throw new Error("Commission activity returned an invalid response.");
  }
  return payload;
}

function timezoneIsValid(timezone) {
  try { return Boolean(timezone && new Intl.DateTimeFormat("en-US", { timeZone: timezone })); }
  catch { return false; }
}

const RECONCILIATION_MESSAGES = {
  subscription_accounting_pending: "Some successful subscription payments have not yet been recorded as commission activity.",
  subscription_actual_fees_pending: "Stripe has not yet provided the actual processing fees for some subscription payments.",
  subscription_accounting_manual_review: "Some subscription payments need manual accounting review.",
  stripe_full_ledger_unavailable: "Stripe transaction history has not been fully imported.",
  provider_ledger_incomplete: "Stripe transaction history is incomplete.",
  reconciliation_variance_requires_opening_balance_and_complete_provider_ledger: "A verified opening balance and complete Stripe history are required to calculate the difference.",
  host_liability_requires_reconciliation: "Host balances need review before a platform payout can be made.",
  host_balance_reconciliation_required: "Host balances need review before a platform payout can be made.",
  provider_finance_requires_reconciliation: "A Stripe refund or dispute needs review before payout.",
  booking_collection_currency_and_livemode_unverified: "Some booking collections have not been verified against Stripe currency and payment mode.",
  subscription_collection_currency_and_livemode_unverified: "Some subscription collections have not been verified against Stripe currency and payment mode.",
  booking_collection_provider_status_unverified: "Some booking payment statuses still need verification against Stripe.",
  subscription_collection_provider_status_unverified: "Some subscription payment statuses still need verification against Stripe.",
  commission_activity_duplicate_projection: "Duplicate commission records need review.",
  commission_activity_conflicting_projection: "Commission records disagree about the amount.",
  invalid_payout_amount: "A host payout has an invalid amount and needs review.",
  payout_operation_requires_reconciliation: "A host payout has an unresolved provider or accounting status.",
  legacy_payout_evidence_unverified: "Historical host payouts have not been matched to current Stripe evidence.",
  unsupported_wallet_currency: "A host wallet contains a currency that cannot be included in USD payouts.",
  legacy_payout_unverified: "Historical host payouts need verification against Stripe.",
  legacy_currency_unverified: "Historical wallet entries have an unverified currency.",
  wallet_snapshot_mismatch: "A host wallet balance does not match its transaction history.",
};

export function reconciliationReasonMessage(code) {
  if (RECONCILIATION_MESSAGES[code]) return RECONCILIATION_MESSAGES[code];
  const words = String(code || "finance review required").replace(/[_-]+/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1) + ".";
}

export function reconciliationDetails(summary) {
  const reconciliation = summary?.reconciliation || {};
  const reasons = [...(reconciliation.reasonCodes || []),
    ...(reconciliation.providerLedger?.reasonCodes || []),
    ...(reconciliation.accountingLinkage?.reasonCodes || []),
    ...(reconciliation.hostReasonCodes || [])];
  return [...new Set(reasons)].map((code) => ({ code, message: reconciliationReasonMessage(code) }));
}
